/**
 * LCFS — one surface for the book-and-claim allocation and the two rosters it
 * merges.
 *
 * These four screens are one workflow, not four destinations. The ledger is the
 * merge of two ordered rosters; the lot inventory is the queue it consumes; the
 * FSE and dairy registries are the rosters themselves. Giving each its own
 * top-level nav entry spread one job across four sidebar rows and buried the
 * lot inventory behind an in-page link, so a reader had to already know the
 * shape of the allocation to find its inputs. One tab, four sections, in the
 * order the allocation runs: output, queue, demand-side roster, supply-side
 * roster.
 *
 * The panels are the same page components as before and each still owns its own
 * query, filters and period selector — this is a container, not a rewrite.
 * `keepMounted={false}` keeps that true: only the visible panel is mounted, so
 * switching sections does not fire four requests or leave three hidden period
 * selectors holding stale state.
 *
 * URL is state of record (locked convention). `?tab=` selects the section and
 * each section's own params ride alongside it, so a filtered roster or a chosen
 * period is still linkable. Switching sections carries only the params the
 * destination understands — `period` follows the ledger into the lot inventory,
 * because they are two views of the same month, while an FSE filter does not
 * leak into the dairy roster.
 */

import { Stack, Tabs, Text, Title } from "@mantine/core";
import {
  IconBuildingFactory2,
  IconBuildingWarehouse,
  IconRoute,
  IconScale,
} from "@tabler/icons-react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { AllocationLedgerPage } from "./AllocationLedger";
import { FseRegistryPage } from "./FseRegistry";
import { LotInventoryPage } from "./LotInventory";
import { PathwayRegistryPage } from "./PathwayExposure";

const TABS = ["allocation", "lots", "fse", "dairies"] as const;
export type LcfsTab = (typeof TABS)[number];

const TAB_VALUES: ReadonlySet<string> = new Set(TABS);

const DEFAULT_TAB: LcfsTab = "allocation";

/**
 * The search params each section owns.
 *
 * Used to decide what survives a section switch. `period` appears twice on
 * purpose: the ledger and the lot inventory are the same month seen from either
 * side, so carrying it between them is the whole point of them sitting
 * together. Anything not listed for the destination is dropped rather than
 * carried, so a stale `status=pending` cannot silently filter a roster the
 * reader has just arrived at.
 */
const TAB_PARAMS: Record<LcfsTab, readonly string[]> = {
  // `lcfsLoadJob` rides along deliberately, unlike the drill-down drawer params:
  // a period load takes minutes, and a reader who switches to the lot inventory
  // while it runs must not lose the handle to it.
  allocation: ["period", "lcfsLoadJob"],
  lots: ["period"],
  fse: ["dispenser", "utility"],
  dairies: ["status"],
};

export function LcfsPage() {
  const [params, setParams] = useSearchParams();

  // An unrecognized `tab` — bookmarked, hand-edited or stale — falls back to
  // the default rather than reaching Mantine, which would select nothing and
  // render every panel hidden.
  const requested = params.get("tab");
  const active: LcfsTab =
    requested && TAB_VALUES.has(requested) ? (requested as LcfsTab) : DEFAULT_TAB;

  const setActive = (tab: string | null) => {
    if (!tab || !TAB_VALUES.has(tab)) return;
    const next = new URLSearchParams();
    next.set("tab", tab);
    for (const key of TAB_PARAMS[tab as LcfsTab]) {
      const value = params.get(key);
      if (value) next.set(key, value);
    }
    setParams(next, { replace: true });
  };

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Title order={2}>LCFS</Title>
        <Text c="dimmed" size="sm" maw={860}>
          The D-RNG book-and-claim allocation and the two rosters it merges. Dispensed volume is
          attributed back to individual dairies — and thus to pathway codes — by drawing an ordered
          queue of dairy-vintage lots against station capacities in dispensing-feed order. The
          ledger is that merge, the lot inventory is the queue behind it, and the two registries are
          the rosters it runs on.
        </Text>
      </Stack>

      <Tabs value={active} onChange={setActive} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab
            value="allocation"
            data-tour="lcfs-tab-allocation"
            leftSection={<IconScale size={14} />}
          >
            Allocation ledger
          </Tabs.Tab>
          <Tabs.Tab
            value="lots"
            data-tour="lcfs-tab-lots"
            leftSection={<IconBuildingWarehouse size={14} />}
          >
            Lot inventory
          </Tabs.Tab>
          <Tabs.Tab
            value="fse"
            data-tour="lcfs-tab-fse"
            leftSection={<IconBuildingFactory2 size={14} />}
          >
            FSE registry
          </Tabs.Tab>
          <Tabs.Tab
            value="dairies"
            data-tour="lcfs-tab-dairies"
            leftSection={<IconRoute size={14} />}
          >
            Dairies &amp; pathways
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="allocation" pt="md">
          <AllocationLedgerPage />
        </Tabs.Panel>
        <Tabs.Panel value="lots" pt="md">
          <LotInventoryPage />
        </Tabs.Panel>
        <Tabs.Panel value="fse" pt="md">
          <FseRegistryPage />
        </Tabs.Panel>
        <Tabs.Panel value="dairies" pt="md">
          <PathwayRegistryPage />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

/**
 * Keeps the old per-screen routes working.
 *
 * `/lcfs/allocation`, `/lcfs/allocation/lots`, `/lcfs/fse` and `/lcfs/pathways`
 * were live before these screens became sections, and the query string is where
 * the view actually lives — so a bookmarked `?period=2026-03` has to survive the
 * hop, not just the path. Replaces rather than pushes, so Back does not land the
 * reader on a URL that immediately redirects again.
 */
export function LcfsTabRedirect({ tab }: { tab: LcfsTab }) {
  const { search } = useLocation();
  const next = new URLSearchParams(search);
  next.set("tab", tab);
  return <Navigate to={{ pathname: "/lcfs", search: next.toString() }} replace />;
}
